import { useEffect, useState } from "react";
import { Activity, Eye, ShieldOff } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ACTION_COLORS = {
  "connexion": "bg-blue-100 text-blue-700",
  "connexion refusée": "bg-red-100 text-red-700",
  "connexion bloquée": "bg-red-100 text-red-700",
  "blocage IP": "bg-red-100 text-red-700",
  "déblocage IP": "bg-green-100 text-green-700",
  "blocage pays": "bg-red-100 text-red-700",
  "création produit": "bg-green-100 text-green-700",
  "modification produit": "bg-amber-100 text-amber-700",
  "suppression produit": "bg-red-100 text-red-700",
  "statut commande": "bg-purple-100 text-purple-700",
  "config Stripe": "bg-indigo-100 text-indigo-700",
  "config SEO": "bg-teal-100 text-teal-700",
};

const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function SuperActivity() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("actions");

  useEffect(() => {
    api.get("/superadmin/activity").then(({ data }) => setData(data)).catch(() => setData({ actions: [], recent_views: [], refused_logins: [] }));
  }, []);

  const refused = data?.refused_logins || [];

  if (user?.role !== "superadmin") return <p className="text-graphite">Accès réservé au super administrateur.</p>;

  return (
    <div data-testid="super-activity">
      <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Super Admin</p>
      <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight mb-6">Journal d'activité</h1>

      <div className="flex gap-2 mb-6">
        <button data-testid="tab-actions" onClick={() => setTab("actions")} className={`px-4 py-2 text-xs uppercase tracking-[0.12em] rounded-sm ${tab === "actions" ? "bg-ink text-cream" : "bg-white border border-stone text-graphite"}`}>
          Actions
        </button>
        <button data-testid="tab-views" onClick={() => setTab("views")} className={`px-4 py-2 text-xs uppercase tracking-[0.12em] rounded-sm ${tab === "views" ? "bg-ink text-cream" : "bg-white border border-stone text-graphite"}`}>
          Visites & parcours
        </button>
        <button data-testid="tab-refused" onClick={() => setTab("refused")} className={`px-4 py-2 text-xs uppercase tracking-[0.12em] rounded-sm flex items-center gap-2 ${tab === "refused" ? "bg-ink text-cream" : "bg-white border border-stone text-graphite"}`}>
          Connexions refusées
          {refused.length > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${tab === "refused" ? "bg-cream text-ink" : "bg-red-100 text-red-700"}`}>{refused.length}</span>}
        </button>
      </div>

      {!data ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-12 bg-white border border-stone animate-pulse rounded-sm" />)}</div>
      ) : tab === "actions" ? (
        <div className="bg-white border border-stone rounded-sm overflow-hidden">
          {data.actions.length === 0 ? (
            <p className="p-8 text-center text-graphite">Aucune action enregistrée.</p>
          ) : (
            <div className="divide-y divide-stone">
              {data.actions.map((a) => (
                <div key={a.id} className="flex items-center gap-3 p-3.5 text-sm">
                  <Activity className="h-4 w-4 text-ash shrink-0" strokeWidth={1.5} />
                  <span className={`text-[11px] px-2 py-1 rounded-sm shrink-0 ${ACTION_COLORS[a.action] || "bg-sand text-graphite"}`}>{a.action}</span>
                  <span className="flex-1 text-ink truncate">{a.detail} {a.target && <span className="text-ash">· {a.target}</span>}</span>
                  <span className="text-ash text-xs hidden sm:block">{a.actor_email}</span>
                  <span className="text-ash text-xs font-mono shrink-0">{fmt(a.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : tab === "refused" ? (
        <div className="bg-white border border-stone rounded-sm overflow-hidden" data-testid="refused-logins">
          <p className="px-4 py-3 text-xs text-graphite border-b border-stone bg-sand/40">
            Tentatives de connexion refusées : comptes admin ou clients bloqués, désactivés ou verrouillés, et tentatives stoppées par le blocage IP / pays avant même d'atteindre le formulaire.
          </p>
          {refused.length === 0 ? (
            <p className="p-8 text-center text-graphite">Aucune connexion refusée.</p>
          ) : (
            <div className="divide-y divide-stone">
              {refused.map((a) => (
                <div key={a.id} className="flex items-center gap-3 p-3.5 text-sm">
                  <ShieldOff className="h-4 w-4 text-red-500 shrink-0" strokeWidth={1.5} />
                  <span className={`text-[11px] px-2 py-1 rounded-sm shrink-0 ${ACTION_COLORS[a.action] || "bg-sand text-graphite"}`}>{a.action}</span>
                  {a.user_role && (
                    <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm bg-sand text-graphite shrink-0">
                      {a.user_role === "customer" ? "client" : a.user_role}
                    </span>
                  )}
                  <span className="flex-1 text-ink truncate">{a.detail}</span>
                  {a.bot_type && (
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-sm border shrink-0 ${a.bot_type === "verified" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : a.bot_type === "impostor" ? "bg-red-100 text-red-700 border-red-200" : "bg-stone/50 text-graphite border-stone"}`}>
                      {a.bot_type === "verified" ? `${a.bot_name} vérifié` : a.bot_type === "impostor" ? `faux ${a.bot_name}` : `robot · ${a.bot_name || "?"}`}
                    </span>
                  )}
                  {a.ip && <span className="text-ash text-xs font-mono hidden md:block">{a.ip}</span>}
                  <span className="text-ash text-xs font-mono shrink-0">{fmt(a.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border border-stone rounded-sm overflow-hidden">
          {data.recent_views.length === 0 ? (
            <p className="p-8 text-center text-graphite">Aucune visite enregistrée.</p>
          ) : (
            <div className="divide-y divide-stone">
              {data.recent_views.map((v) => (
                <div key={v.id} className="flex items-center gap-3 p-3.5 text-sm">
                  <Eye className="h-4 w-4 text-ash shrink-0" strokeWidth={1.5} />
                  <span className="text-[11px] px-2 py-1 rounded-sm bg-sand text-graphite shrink-0">{v.kind}</span>
                  <span className="flex-1 text-ink truncate font-mono text-xs">{v.path}</span>
                  <span className="text-ash text-xs hidden sm:block truncate max-w-[140px]">sess. {v.session_id?.slice(0, 8)}</span>
                  <span className="text-ash text-xs font-mono shrink-0">{fmt(v.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
